import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CreditCard, BarChart3, Users, FileText, Sparkles, ExternalLink, CheckCircle2, XCircle, Calendar } from 'lucide-react';
import { billingAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import { useCountUp } from '../hooks/useCountUp';

const PLAN_PRICE        = { starter: '0 €',   pro: '49 €',  team: '199 €', enterprise: 'Sur mesure' };
const PLAN_PRICE_NUM    = { pro: 49, team: 199 };
const PLAN_PILL_TONE    = { starter: '',      pro: 'blue',  team: 'success', enterprise: 'violet' };

const STATUS_PILL = {
  active:   { tone: 'success', label: 'Actif' },
  trialing: { tone: 'blue',    label: 'Essai' },
  past_due: { tone: 'danger',  label: 'Paiement en retard' },
  canceled: { tone: '',        label: 'Annulé' },
};

const formatDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const Billing = () => {
  const { currentOrg } = useAuth();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([billingAPI.getSummary(), billingAPI.getPlans()]);
      setSummary(s); setPlans(p);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de charger les informations de facturation', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (currentOrg) fetchBilling(); else setLoading(false); }, [currentOrg, fetchBilling]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') toast({ title: 'Abonnement activé !', description: 'Votre abonnement a été configuré avec succès.' });
    else if (checkout === 'cancel') toast({ title: 'Paiement annulé', description: 'Le processus de paiement a été annulé.', variant: 'destructive' });
  }, [searchParams]);

  const handleCheckout = async (planCode) => {
    if (!['pro', 'team'].includes(planCode)) {
      toast({ title: 'Plan invalide', description: 'Sélectionnez un plan Pro ou Team.', variant: 'destructive' });
      return;
    }
    setCheckoutLoading(true);
    try {
      const { checkout_url } = await billingAPI.createCheckout(planCode);
      window.location.href = checkout_url;
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setCheckoutLoading(false); }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await billingAPI.openPortal();
      window.location.href = portal_url;
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setPortalLoading(false); }
  };

  const plan = summary?.plan;
  const subscription = summary?.subscription;
  const usage = summary?.usage || {};
  const planCode = plan?.code || 'starter';
  const planLabel = plan?.name || 'Starter';
  const subStatus = subscription?.status;
  const subStatusCfg = subStatus ? STATUS_PILL[subStatus] || STATUS_PILL.canceled : null;

  // Animated counters (no-ops to 0 when summary not yet loaded)
  const seatsCount    = useCountUp(usage.active_seats || 0);
  const aiCount       = useCountUp(usage.ai_parse_count || 0);
  const exportsCount  = useCountUp(usage.pdf_export_count || 0);

  // Usage limits per current plan
  const aiLimit       = planCode === 'pro' ? 100 : (planCode === 'team' || planCode === 'enterprise' ? null : 1);
  const aiLimitSuffix = (!plan || planCode === 'starter') ? '/jour' : '/mois';
  const seatLimit     = plan?.max_seats > 0 ? plan.max_seats : null;
  const dossierLimit  = plan?.max_dossiers > 0 ? plan.max_dossiers : null;

  return (
    <AppLayout title="Facturation">
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>

        {/* Page head */}
        <div className="sd-page-head" style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <CreditCard size={19} style={{ color: 'var(--vio2)' }} />
              Facturation
            </h1>
            <p>{currentOrg?.name || '—'} · {summary?.has_subscription ? 'Abonnement actif' : 'Plan gratuit Starter'}</p>
          </div>
          {summary?.has_subscription && (
            <button onClick={handlePortal} disabled={portalLoading} className="sd-btn sd-btn-ghost">
              <ExternalLink size={12} /> {portalLoading ? 'Chargement…' : "Gérer l'abonnement"}
            </button>
          )}
        </div>

        {loading ? (
          <div className="sd-stats-row">
            {[1,2,3,4].map(i => <div key={i} className="sd-stat-skeleton" />)}
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div className="sd-stats-row sd-stagger">
              <KpiCard
                label="Plan actuel"
                value={planLabel}
                sub={PLAN_PRICE[planCode] ? `${PLAN_PRICE[planCode]}${planCode !== 'enterprise' ? ' / mois' : ''}` : ''}
                icon={<CreditCard size={16} />}
                pill={subStatusCfg ? <span className={`sd-pill ${subStatusCfg.tone}`}>{subStatusCfg.label}</span> : null}
                tone="violet"
              />
              <KpiCard
                label="Membres actifs"
                value={`${seatsCount}${seatLimit ? ` / ${seatLimit}` : ''}`}
                sub={seatLimit ? `${Math.min(100, Math.round((usage.active_seats || 0) / seatLimit * 100))}% utilisés` : 'illimité'}
                icon={<Users size={16} />}
                tone="blue"
              />
              <KpiCard
                label="Analyses IA"
                value={`${aiCount}${aiLimit ? ` / ${aiLimit}` : ''}`}
                sub={aiLimit ? `${aiLimitSuffix} · ${Math.min(100, Math.round((usage.ai_parse_count || 0) / aiLimit * 100))}%` : 'illimité'}
                icon={<Sparkles size={16} />}
                tone="violet"
              />
              <KpiCard
                label="Exports PDF"
                value={exportsCount}
                sub={dossierLimit ? `Dossiers ce mois · max ${dossierLimit}` : 'illimité'}
                icon={<FileText size={16} />}
                tone="green"
              />
            </div>

            {/* Two-column grid */}
            <div className="sd-page-grid">

              {/* LEFT — current plan + usage breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="sd-dash-section">
                  <div className="sd-dash-section-head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CheckCircle2 size={14} style={{ color: 'var(--vio2)' }} />
                      <span className="sd-dash-section-title">Votre abonnement</span>
                    </div>
                    {subscription && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--muted2)' }}>
                        <Calendar size={11} />
                        Renouvellement : {formatDate(subscription.current_period_end)}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '16px 20px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--white)', letterSpacing: -0.8 }}>{planLabel}</span>
                      <span style={{ fontSize: 14, color: 'var(--muted2)' }}>{PLAN_PRICE[planCode]}{planCode !== 'enterprise' ? ' / mois' : ''}</span>
                    </div>
                    {subscription?.cancel_at_period_end && (
                      <p style={{ fontSize: 11.5, color: '#FBBF24', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 12 }}>
                        <XCircle size={11} /> Annulation prévue à la fin de la période
                      </p>
                    )}
                    {!summary?.has_subscription && (
                      <p style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 14 }}>
                        4 dossiers/mois · 1 utilisateur. Passez à Pro pour débloquer les exports PPT, le partage et la personnalisation.
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!summary?.has_subscription ? (
                        <>
                          <button onClick={() => handleCheckout('pro')} disabled={checkoutLoading} className="sd-btn sd-btn-primary">
                            <CreditCard size={12} /> Passer à Pro — 49 €/mois
                          </button>
                          <button onClick={() => handleCheckout('team')} disabled={checkoutLoading} className="sd-btn sd-btn-ghost">
                            Passer à Team — 199 €/mois
                          </button>
                        </>
                      ) : (
                        <button onClick={handlePortal} disabled={portalLoading} className="sd-btn sd-btn-soft">
                          <ExternalLink size={12} /> {portalLoading ? 'Chargement…' : "Gérer l'abonnement"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="sd-dash-section">
                  <div className="sd-dash-section-head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <BarChart3 size={14} style={{ color: 'var(--vio2)' }} />
                      <span className="sd-dash-section-title">Utilisation ce mois</span>
                    </div>
                  </div>
                  <div style={{ padding: '8px 20px 18px' }}>
                    <UsageBar
                      icon={<Users size={13} />}
                      label="Membres actifs"
                      value={usage.active_seats || 0}
                      limit={seatLimit}
                      color="#60A5FA"
                    />
                    <UsageBar
                      icon={<Sparkles size={13} />}
                      label="Analyses IA"
                      value={usage.ai_parse_count || 0}
                      limit={aiLimit}
                      limitSuffix={aiLimitSuffix}
                      color="#A099E8"
                    />
                    <UsageBar
                      icon={<FileText size={13} />}
                      label="Exports PDF"
                      value={usage.pdf_export_count || 0}
                      limit={null}
                      color="#4ADE80"
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT — plan picker */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="sd-dash-section">
                  <div className="sd-dash-section-head">
                    <span className="sd-dash-section-title">Plans disponibles</span>
                  </div>
                  <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {plans.map((p) => {
                      const isCurrent = p.code === planCode;
                      const isPopular = p.code === 'pro';
                      const price = PLAN_PRICE[p.code] || '';
                      const tone = PLAN_PILL_TONE[p.code] || '';
                      return (
                        <div key={p.id} style={{
                          background: isPopular ? 'rgba(123,104,238,0.06)' : 'var(--surface2)',
                          border: `1px solid ${isPopular ? 'rgba(123,104,238,0.35)' : 'var(--border)'}`,
                          borderRadius: 9, padding: '12px 14px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{p.name}</span>
                              {isPopular && <span className="sd-pill violet">Populaire</span>}
                              {isCurrent && <span className="sd-pill success">Actuel</span>}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>
                              {price}
                              {p.code !== 'enterprise' && <span style={{ fontSize: 10.5, color: 'var(--muted2)', fontWeight: 400 }}> /mois</span>}
                            </div>
                          </div>
                          <p style={{ fontSize: 11, color: 'var(--muted2)', marginBottom: 10 }}>
                            {p.max_seats > 0 ? `${p.max_seats} utilisateur${p.max_seats > 1 ? 's' : ''}` : 'Utilisateurs illimités'}
                            {' · '}
                            {p.max_dossiers > 0 ? `${p.max_dossiers} dossiers/mois` : 'Dossiers illimités'}
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
                            <PlanFeature label="Analyse IA de CV" included={p.includes_ai_parsing} />
                            <PlanFeature label="Export PPT & lien partageable" included={p.includes_pptx_export || p.includes_share_link} />
                            <PlanFeature label="Branding personnalisé" included={p.includes_custom_branding} />
                            <PlanFeature label="SSO / SAML" included={p.includes_sso} />
                          </div>
                          {p.stripe_price_id && p.code !== 'starter' && !isCurrent && (
                            <button
                              onClick={() => handleCheckout(p.code)}
                              disabled={checkoutLoading}
                              className={`sd-btn ${isPopular ? 'sd-btn-primary' : 'sd-btn-ghost'}`}
                              style={{ width: '100%', justifyContent: 'center' }}
                            >
                              {checkoutLoading ? 'Redirection…' : 'Choisir ce plan'}
                            </button>
                          )}
                          {!p.stripe_price_id && p.code === 'enterprise' && !isCurrent && (
                            <button
                              onClick={() => window.location.href = 'mailto:contact@skilldeck.com'}
                              className="sd-btn sd-btn-ghost"
                              style={{ width: '100%', justifyContent: 'center' }}
                            >
                              Contactez-nous
                            </button>
                          )}
                          {isCurrent && (
                            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)' }}>
                              Votre plan actuel
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

const KpiCard = ({ label, value, sub, icon, tone, pill }) => {
  const toneCss = {
    violet: { bg: 'rgba(108,71,255,0.1)', color: 'var(--vio2)' },
    green:  { bg: 'rgba(34,197,94,0.1)',  color: '#4ADE80' },
    amber:  { bg: 'rgba(249,115,22,0.1)', color: '#FB923C' },
    blue:   { bg: 'rgba(59,130,246,0.1)', color: '#60A5FA' },
  }[tone] || { bg: 'rgba(108,71,255,0.1)', color: 'var(--vio2)' };
  return (
    <div className="sd-stat-card">
      <div className="sd-stat-label">{label}</div>
      <div className="sd-stat-value" style={{ fontSize: 22 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        {pill || (sub && <span className="sd-stat-delta neutral">{sub}</span>)}
      </div>
      <div className="sd-stat-icon" style={{ background: toneCss.bg, color: toneCss.color }}>{icon}</div>
    </div>
  );
};

const UsageBar = ({ icon, label, value, limit, limitSuffix = '', color }) => {
  const pct = limit ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--muted2)' }}>
          <span style={{ color }}>{icon}</span> {label}
        </div>
        <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600 }}>
          {value}{limit ? <span style={{ color: 'var(--muted2)', fontWeight: 400 }}> / {limit}{limitSuffix}</span> : <span style={{ color: 'var(--muted)', fontSize: 10.5, fontWeight: 400 }}> illimité</span>}
        </div>
      </div>
      {limit && (
        <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
        </div>
      )}
    </div>
  );
};

const PlanFeature = ({ label, included }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
    {included
      ? <CheckCircle2 size={12} style={{ color: '#4ADE80', flexShrink: 0 }} />
      : <XCircle size={12} style={{ color: 'var(--muted)', flexShrink: 0 }} />}
    <span style={{ color: included ? 'var(--white)' : 'var(--muted)' }}>{label}</span>
  </div>
);

export default Billing;
